import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { Dialog } from "@mui/material";
import {
    ChangePasswordObject, useChangePasswordMutation,
} from "../../../services/grace";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PasswordValidator from "password-validator";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';

export interface ChangePasswordDialogValue {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

export interface ChangePasswordDialogProps {
    open: boolean;
    preventClose?: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

export default function ChangePasswordDialog(props: ChangePasswordDialogProps): JSX.Element {
    const {
        onClose,
        onSubmit,
        preventClose = false,
        ...other
    } = props;

    const alertDispatch = useAlertDispatch();

    const inputWidth = {
        width: 540,
    }

    const [inProgress, setInProgress] = useState(false);

    const [changePassword] = useChangePasswordMutation();

    const [formValues, setFormValues] = useState<ChangePasswordDialogValue>({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [criteriaFlags, setCriteriaFlags] = useState<any>([]);

    useEffect(() => {
        const schema = new PasswordValidator();

        schema
            .is().min(10)
            .has().symbols()
            .has().digits()
            .has().uppercase()
            .has().lowercase();

        const result = schema.validate(formValues.newPassword, { list: true });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorList = result as any[];

        if (formValues.newPassword === "" || formValues.newPassword !== formValues.confirmNewPassword) {
            errorList.push("match");
        }

        setCriteriaFlags(errorList);
    }, [formValues]);

    const handleClose = () => {
        if (!inProgress && !preventClose) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (formValues.currentPassword === "" || formValues.newPassword === "" || formValues.confirmNewPassword === "") {
            return;
        }

        if (criteriaFlags.length !== 0) {
            return;
        }

        const payload: ChangePasswordObject = {
            currentPassword: formValues.currentPassword,
            newPassword: formValues.newPassword,
        }

        try {
            setInProgress(true);
            await changePassword(payload).unwrap();
            setFormValues({
                currentPassword: "",
                newPassword: "",
                confirmNewPassword: "",
            });
            onSubmit();
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to update password",
                    }
                });
            }
        }

        setInProgress(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        });
    };

    return (
        <Dialog
            {...other}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Change password</DialogTitle>
            <DialogContent>
                <Grid container sx={{
                    flex: 1,
                    spacing: 1,
                }}>
                    <Grid item xs={12}>
                        <FormControl sx={{ margin: 1 }}>
                            <TextField
                                sx={{ inputWidth }}
                                fullWidth
                                label="Enter current password"
                                name="currentPassword"
                                error={formValues.currentPassword === ''}
                                required
                                type='password'
                                value={formValues.currentPassword}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl sx={{ margin: 1 }}>
                            <TextField
                                sx={{ inputWidth }}
                                fullWidth
                                label='Enter new password'
                                name='newPassword'
                                error={criteriaFlags.length > 0}
                                required
                                type='password'
                                value={formValues.newPassword}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl sx={{ margin: 1 }}>
                            <TextField
                                sx={{ inputWidth }}
                                fullWidth
                                label="Confirm new password"
                                name="confirmNewPassword"
                                error={criteriaFlags.length > 0}
                                required
                                type='password'
                                value={formValues.confirmNewPassword}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ height: "20px" }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            New passwords must:
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("match") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("match") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Match
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("min") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("min") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Be at least 10 characters
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("symbols") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("symbols") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Contain non-alphanumeric characters (!@#$%^&)
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("digits") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("digits") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Contain numeric characters (0123456789)
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("uppercase") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("uppercase") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Contain uppercase characters (ABC)
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {!criteriaFlags.includes("lowercase") &&
                            <FontAwesomeIcon icon={['far', 'check-circle']} color="green" title="Meet's criteria?" />
                        }
                        {criteriaFlags.includes("lowercase") &&
                            <FontAwesomeIcon icon={['far', 'times-circle']} color="red" title="Meet's criteria?" />
                        }
                        <Typography display="inline" variant="subtitle2" sx={{ marginLeft: "5px" }}>
                            Contain lowercase characters (abc)
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {inProgress &&
                    <CircularProgress size={20} color={"secondary"} />
                }
                {!preventClose &&
                    <Button onClick={handleClose} disabled={inProgress} color="primary">
                        Cancel
                    </Button>
                }
                <Button
                    onClick={handleSubmit}
                    disabled={
                        formValues.currentPassword === ""
                        || criteriaFlags.length > 0
                        || inProgress
                    }
                    color="primary" autoFocus
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}
